import type { LinearDimensionDefinition } from "building-types/annotation/annotationTypes";
import { DimLabelPlacementType } from "building-types/annotation/annotationTypes";
import type { PlacementXAnchor } from "building-types/annotation/annotationTypesEnum";
import { DIM_EXT, WITNESS_LINE_GAP } from "building-types/constants/dimensionConstants";
import { orientedTickAtPoint } from "building-types/dimensions/dimensionComponentTools";
import type {
  Dimension,
  DimensionBase,
  DimensionGeometryProps,
  GeneratedDimensionPlacementType,
} from "building-types/dimensions/dimensionTypes";
import type { DimensionRowLevel } from "building-types/dimensions/dimensionTypesEnum";
import type { AutoDimensionRuntimeHandle } from "building-types/handles/docEntityHandleTypes";
import { DimensionPrecision, DimensionPrecisionDenominator } from "building-types/views/viewTypes";
import type { PartialBy } from "common/utils/partialBy";
import { aabb2ByPolyLine2s } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { ANGLE_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { line2Normal, line2Tangent, line2CanonicalTangent } from "geom/line2";
import { lineLength, line2VecPerp, lineStartPt, lineEndPt } from "geom/line2Polyline2";
import {
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2EdgeEval,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2FromCubicBezier,
  polyLine2Reverse,
  polyLine2ReverseCopy,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { getLine } from "geom/rect2";
import type { Vec2 } from "geom/vec2";
import { vec2Add, vec2DistSquared, vec2Dot, vec2Scale, vec2Sub } from "geom/vec2";
import { formatInchesFraction, isBelowInchFractionTolerance } from "./measurementUtils";
import type { AlignmentInfo, PositionInfo } from "./plans/overlapping";
import { fixOverlappingOrientedItemsInRows } from "./plans/overlapping";

export function dimensionNew({
  labelPlacementType = DimLabelPlacementType.EDGE,
  dimLine,
  baseDimLine,
  label,
  labelUnstacked,
  textDirXOverride,
  textDirYOverride,
  linework,
  witnessPls,
  ticks,
  labelMidPtX,
  labelMidPtY,
  labelPerimeter,
  labelAABB,
  rangeAABB,
  boxVerticalAlignmentPercentageOffset,
  placementXAnchor,
  halfEdges,
  rowLevel,
  handle,
  startAnchor,
  endAnchor,
  autoKind,
  startExt,
  endExt,
  witnessLineGap,
  dimStrip,
}: PartialBy<Dimension, "labelPlacementType">): Dimension {
  return {
    labelPlacementType,
    dimLine,
    baseDimLine,
    linework,
    witnessPls,
    ticks,
    label,
    labelUnstacked,
    labelMidPtX,
    labelMidPtY,
    labelPerimeter,
    labelAABB,
    rangeAABB,
    textDirXOverride,
    textDirYOverride,
    boxVerticalAlignmentPercentageOffset,
    placementXAnchor,
    halfEdges,
    rowLevel,
    handle,
    startAnchor,
    endAnchor,
    autoKind,
    startExt,
    endExt,
    witnessLineGap,
    dimStrip,
  };
}

function makeDimLine(line: PolyLine2, pVec: PolyLine2, dimL: number): PolyLine2 {
  const dimLine = polyLine2New(4);
  dimLine[0] = line[0] + pVec[0] * dimL;
  dimLine[1] = line[1] + pVec[1] * dimL;
  dimLine[2] = line[2] + pVec[0] * dimL;
  dimLine[3] = line[3] + pVec[1] * dimL;

  return dimLine;
}

export function getDimLabel(
  dimLine: PolyLine2,
  dimensionPrecision = DimensionPrecision.PRECISION_1_8,
  stacked: boolean = true,
): string {
  return formatWithPrecision(lineLength(dimLine), dimensionPrecision, false, stacked);
}

export function formatWithPrecision(
  inches: number,
  dimensionPrecision = DimensionPrecision.PRECISION_1_8,
  allowZeroFeet: boolean = false,
  stackFractions: boolean = false,
): string {
  if (dimensionPrecision === DimensionPrecision.FOOT) {
    const feet = Math.round(inches / 12);
    return `${feet}\'`;
  }

  return formatInchesFraction(inches, DimensionPrecisionDenominator[dimensionPrecision], allowZeroFeet, stackFractions);
}

export function getLinearDimensionLabel(
  dim: LinearDimensionDefinition,
  stacked: boolean = true,
  dimensionPrecision = DimensionPrecision.PRECISION_1_8,
): string {
  const { dimensionValue = "", prefix = "", suffix = "" } = dim;

  let label = "";

  if (prefix.length > 0) {
    label += `${prefix}  `;
  }

  label +=
    dimensionValue.length > 0
      ? dimensionValue
      : getDimLabel(
          polyLine2ByTwoPoints(dim.lineStartX, dim.lineStartY, dim.lineEndX, dim.lineEndY),
          dimensionPrecision,
          stacked,
        );

  if (suffix.length > 0) {
    label += `  ${suffix}`;
  }

  return label;
}

export function getOrientedDimLine(dimLine: PolyLine2): PolyLine2 {
  const aabb = aabb2ByPolyLine2s([dimLine]);
  if (!aabb) {
    return polyLine2ByTwoPoints(0, 0, 0, 0);
  }

  let line: PolyLine2;

  const [xn, yn] = line2Normal(dimLine[0], dimLine[1], dimLine[2], dimLine[3], true);
  const xnAbs = Math.abs(xn);
  const ynAbs = Math.abs(yn);

  if (xnAbs <= ANGLE_EPSILON) {
    line = polyLine2ByTwoPoints(aabb.minX, aabb.minY, aabb.maxX, aabb.maxY);
  } else if (ynAbs <= ANGLE_EPSILON) {
    line = polyLine2ByTwoPoints(aabb.minX, aabb.minY, aabb.maxX, aabb.maxY);
  } else {
    if (Math.abs(xnAbs - ynAbs) < ANGLE_EPSILON || xnAbs < ynAbs) {
      if (yn >= 0) {
        line = polyLine2Copy(dimLine);
      } else {
        line = polyLine2ReverseCopy(dimLine);
      }
    } else {
      if (xn >= 0) {
        line = polyLine2Copy(dimLine);
      } else {
        line = polyLine2ReverseCopy(dimLine);
      }
    }

    if (getDimLineOrientation(line) === DimLineOrientation.LEFT) {
      polyLine2Reverse(line);
    }
  }

  return line;
}

export enum DimLineOrientation {
  TOP,
  LEFT,
}

export function getDimLineOrientation(dimLine: PolyLine2) {
  const [xn, yn] = line2Normal(dimLine[0], dimLine[1], dimLine[2], dimLine[3], true);
  const xnAbs = Math.abs(xn);
  const ynAbs = Math.abs(yn);

  if (Math.abs(xnAbs - ynAbs) < ANGLE_EPSILON || xnAbs < ynAbs) {
    return DimLineOrientation.TOP;
  } else {
    return DimLineOrientation.LEFT;
  }
}

export function getDimLabelPosition(dimLine: PolyLine2): Vec2 {
  return polyLine2EdgeEval(dimLine, 0, 0.5, true);
}

interface DimensionBaseProps extends DimensionGeometryProps {
  readonly labelOverride?: string;
  readonly extendToPl?: boolean;
  readonly witnessLineGap?: number;
  readonly dimensionPrecision?: DimensionPrecision;
}

function makeDimensionBase({
  pl,
  baseLineOffset = 0,
  dimExt = DIM_EXT,
  witnessLineGap = WITNESS_LINE_GAP,
  startExt = 0,
  endExt = 0,
  labelPosX,
  labelPosY,
  labelOverride,
  extendToPl = true,
  baseLine: manualBaseLine,
  dimensionPrecision = DimensionPrecision.PRECISION_1_8,
}: DimensionBaseProps): DimensionBase | undefined {
  const baseLine = manualBaseLine ?? getLine(pl, 0);
  const baseLineLen = lineLength(baseLine);

  if (baseLineLen < DIST_EPSILON) {
    return undefined;
  }

  if (isBelowInchFractionTolerance(baseLineLen)) {
    return undefined;
  }

  const pVec = line2VecPerp(baseLine);
  const line = manualBaseLine ? pl : makeDimLine(baseLine, pVec, baseLineOffset);

  const baseStartPt = lineStartPt(baseLine);
  const baseEndPt = lineEndPt(baseLine);

  const startPt = lineStartPt(line);
  const endPt = lineEndPt(line);

  const dimLine = polyLine2New(4);
  dimLine[0] = startPt[0];
  dimLine[1] = startPt[1];
  dimLine[2] = endPt[0];
  dimLine[3] = endPt[1];

  // ticks

  const [tx, ty] = line2Tangent(dimLine[0], dimLine[1], dimLine[2], dimLine[3]);
  const tickLine1 = orientedTickAtPoint(tx, ty, dimLine[0], dimLine[1]);
  const tickLine2 = orientedTickAtPoint(tx, ty, dimLine[2], dimLine[3]);

  polyLine2ExtendEnd(dimLine, dimExt);
  polyLine2ExtendStart(dimLine, dimExt);

  const linework: PolyLine2[] = [dimLine];

  const witnessPls: PolyLine2[] = [];

  if (vec2DistSquared(baseLine[0], baseLine[1], line[0], line[1]) > DIST_EPSILON && extendToPl) {
    const dim1 = polyLine2New(4);
    dim1[0] = startPt[0];
    dim1[1] = startPt[1];
    dim1[2] = baseStartPt[0];
    dim1[3] = baseStartPt[1];

    const dim2 = polyLine2New(4);
    dim2[0] = endPt[0];
    dim2[1] = endPt[1];
    dim2[2] = baseEndPt[0];
    dim2[3] = baseEndPt[1];

    polyLine2ExtendStart(dim1, dimExt);
    polyLine2ExtendStart(dim2, dimExt);
    polyLine2ExtendEnd(dim1, dimExt + startExt - witnessLineGap);
    polyLine2ExtendEnd(dim2, dimExt + endExt - witnessLineGap);

    witnessPls.push(dim1);
    witnessPls.push(dim2);
  }

  const ticks: PolyLine2[] = [];

  ticks.push(tickLine1, tickLine2);

  const [x, y] = getDimLabelPosition(dimLine);
  const finalLabelPosX = labelPosX !== undefined ? labelPosX : x;
  const finalLabelPosY = labelPosY !== undefined ? labelPosY : y;
  const dimLabel = labelOverride ?? getDimLabel(line, dimensionPrecision);
  const dimLabelUnstacked = labelOverride ?? getDimLabel(line, dimensionPrecision, false);

  return {
    dimLine,
    baseDimLine: line,
    label: dimLabel,
    labelUnstacked: dimLabelUnstacked,
    linework,
    witnessPls,
    ticks,
    labelMidPtX: finalLabelPosX,
    labelMidPtY: finalLabelPosY,
    startAnchor: [baseStartPt[0], baseStartPt[1]],
    endAnchor: [baseEndPt[0], baseEndPt[1]],
  };
}

interface DimensionProps extends DimensionBaseProps {
  readonly labelPerimeter?: PolyLine2;
  readonly labelAABB?: AABB2;
  readonly rowLevel?: DimensionRowLevel;
  readonly handle?: AutoDimensionRuntimeHandle;
  readonly dimensionPrecision?: DimensionPrecision;
  readonly placement?: GeneratedDimensionPlacementType;
  readonly startExt?: number;
  readonly endExt?: number;
  readonly witnessLineGap?: number;
}

export function makeDimensionFromProps(props: DimensionProps): null | Dimension {
  const {
    labelPerimeter,
    labelAABB,
    rangeAABB,
    halfEdges,
    rowLevel,
    handle,
    placement,
    startExt,
    endExt,
    witnessLineGap,
    dimStrip,
  } = props;

  const baseDim = makeDimensionBase(props);

  if (!baseDim) {
    return null;
  }

  return dimensionNew({
    ...baseDim,
    labelPerimeter,
    labelAABB,
    rangeAABB,
    halfEdges,
    rowLevel,
    handle,
    autoKind: placement,
    startExt,
    endExt,
    witnessLineGap,
    dimStrip,
  });
}

/**Return false if not adding a dimension to dim */
export function makeDimension(dims: Dimension[], props: DimensionProps): boolean {
  const dim = makeDimensionFromProps(props);

  if (dim) {
    dims.push(dim);
    return true;
  }
  return true;
}

interface DimensionLeaderProps extends DimensionBaseProps {
  placementXAnchor: PlacementXAnchor;
  textDirX: number;
  textDirY: number;
  dimensionPrecision?: DimensionPrecision;
  autoKind?: GeneratedDimensionPlacementType;
}

/**
 * Creates a dimension with a bezier leader line based on the dimension line normal and the text direction.
 */
export function makeDimensionLeader(dims: Dimension[], props: DimensionLeaderProps): void {
  const baseDimProps = makeDimensionBase(props);

  if (!baseDimProps) {
    return;
  }

  const { labelMidPtX, labelMidPtY, dimLine, linework } = baseDimProps;

  const { placementXAnchor, textDirX, textDirY } = props;

  const [midX, midY] = polyLine2EdgeEval(dimLine, 0, 0.5, true);

  let [dimNormalX, dimNormalY] = line2Normal(dimLine[0], dimLine[1], dimLine[2], dimLine[3]);
  let [xMag, yMag] = vec2Sub(midX, midY, labelMidPtX, labelMidPtY);

  if (vec2Dot(dimNormalX, dimNormalY, xMag, yMag) > 0) {
    [dimNormalX, dimNormalY] = [-dimNormalX, -dimNormalY];
  }

  [xMag, yMag] = vec2Scale(xMag, yMag, 0.75);
  [xMag, yMag] = [Math.abs(xMag), Math.abs(yMag)];

  const leader = polyLine2FromCubicBezier(
    labelMidPtX,
    labelMidPtY,
    labelMidPtX + textDirX * xMag,
    labelMidPtY + textDirY * yMag,
    midX + dimNormalX * xMag,
    midY + dimNormalY * yMag,
    midX,
    midY,
    2,
  );

  linework.push(leader);

  dims.push(
    dimensionNew({
      ...baseDimProps,
      linework,
      labelPlacementType: DimLabelPlacementType.LEADER,
      boxVerticalAlignmentPercentageOffset: 0,
      placementXAnchor,
      textDirXOverride: textDirX,
      textDirYOverride: textDirY,
      autoKind: props.autoKind,
    }),
  );
}

export function fixOverlappingDimsInRows(
  dims: Dimension[],
  verticalGroupTolerance = 1,
  horizontalPadWidth = 15,
  width: (text: string) => number,
): Dimension[] {
  const inPredicate = (dim: Dimension): AlignmentInfo => {
    const { dimLine: line, labelMidPtX, labelMidPtY } = dim;
    const orientation = line2Tangent(line[0], line[1], line[2], line[3]);
    return {
      orientation,
      x: labelMidPtX,
      y: labelMidPtY,
      width: width ? width(dim.label) : horizontalPadWidth,
    };
  };

  const outPredicate = (rowItem: PositionInfo<Dimension>): Dimension => {
    const {
      item: {
        dimLine,
        baseDimLine,
        labelMidPtX: x,
        labelMidPtY: y,
        label,
        labelUnstacked,
        labelAABB,
        labelPerimeter,
        linework,
        witnessPls,
        ticks,
        halfEdges,
        rowLevel,
        handle,
        boxVerticalAlignmentPercentageOffset,
        startAnchor,
        endAnchor,
        autoKind,
        witnessLineGap,
      },
      xAdjust,
      yAdjust,
    } = rowItem;
    const [labelMidPtX, labelMidPtY] = vec2Add(x, y, xAdjust, yAdjust);
    return dimensionNew({
      dimLine,
      baseDimLine,
      label,
      labelUnstacked,
      linework,
      witnessPls,
      ticks,
      labelMidPtX,
      labelMidPtY,
      labelPerimeter,
      labelAABB,
      halfEdges,
      rowLevel,
      handle,
      boxVerticalAlignmentPercentageOffset,
      startAnchor,
      endAnchor,
      autoKind,
      witnessLineGap,
    });
  };

  return fixOverlappingOrientedItemsInRows(dims, verticalGroupTolerance, horizontalPadWidth, inPredicate, outPredicate);
}

/**
 * This function filters dimensions that are "duplicate".  Dimensions are duplicate if the following are true:
 *   Their center points are within DIST_THRESHOLD_SQUARED (currently 4 feet)
 *   The start end values on the tangent line are equal.  (So horizonal lines, the start x and end x must equal).
 */
export function filterDuplicateDims(dims: Dimension[], distThresholdFt: number): Dimension[] {
  const getDimInfo = (l: PolyLine2) => {
    const [absTangentX, absTangentY] = line2CanonicalTangent(l[0], l[1], l[2], l[3]);

    const v1 = vec2Dot(l[0], l[1], absTangentX, absTangentY);
    const v2 = vec2Dot(l[2], l[3], absTangentX, absTangentY);
    const [centerX, centerY] = polyLine2EdgeEval(l, 0, 0.5, true);

    return {
      centerX,
      centerY,
      tangentStart: Math.min(v1, v2),
      tangentEnd: Math.max(v1, v2),
      absTangentX,
      absTangentY,
    };
  };

  const distThresholdSquared = distThresholdFt * 12 * (distThresholdFt * 12);
  const ret: Dimension[] = [];
  const dimInfo = dims.map((dim) => getDimInfo(dim.dimLine));
  const dimCount = dims.length;

  for (let i = 0; i < dimCount; i++) {
    const dimToFilter = dimInfo[i];
    let duplicateFound = false;
    for (let j = i + 1; j < dimCount && !duplicateFound; j++) {
      const dimToCheck = dimInfo[j];

      if (
        vec2DistSquared(dimToFilter.centerX, dimToFilter.centerY, dimToCheck.centerX, dimToCheck.centerY) >
        distThresholdSquared
      ) {
        continue;
      }

      if (
        vec2DistSquared(
          dimToFilter.absTangentX,
          dimToFilter.absTangentY,
          dimToCheck.absTangentX,
          dimToCheck.absTangentY,
        ) > DIST_EPSILON_SQUARED
      ) {
        continue;
      }

      if (
        vec2DistSquared(
          dimToFilter.tangentStart,
          dimToFilter.tangentEnd,
          dimToCheck.tangentStart,
          dimToCheck.tangentEnd,
        ) > DIST_EPSILON_SQUARED
      ) {
        continue;
      }

      duplicateFound = true;
    }
    if (!duplicateFound) {
      ret.push(dims[i]);
    }
  }

  return ret;
}
